import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./diagnosticoPush.module.css";
import { AuthContext } from "../../context/AuthContext";
import api from "../../services/api";
import { registrarPush, MOTIVOS_FALHA_PUSH } from "../../services/push";
import BackIcon from "../../components/BackIcon";
import BottomNav from "../../components/BottomNav";
import ApiButton from "../../components/ApiButton/ApiButton";

// Tela de diagnóstico do registro de push - existe porque em celular não dá
// pra abrir o console do navegador pra ver por que um aparelho específico não
// está gravando em Web.Dispositivos (pedido do usuário, 2026-09-28). Mostra
// na tela o que normalmente só aparece no console, e deixa rodar o registro
// de novo ali mesmo, mostrando o resultado exato.
export default function DiagnosticoPush() {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    const [ambiente, setAmbiente] = useState(null);
    const [testando, setTestando] = useState(false);
    const [resultado, setResultado] = useState(null);

    useEffect(() => {
        coletarAmbiente();
    }, []);

    async function coletarAmbiente() {
        const standalone =
            window.matchMedia?.("(display-mode: standalone)")?.matches ||
            window.navigator.standalone === true;

        const temServiceWorker = "serviceWorker" in navigator;
        const temPushManager = "PushManager" in window;
        const temNotification = typeof Notification !== "undefined";
        const permissao = temNotification ? Notification.permission : "indisponível";

        let temInscricao = false;
        let endpoint = "";
        if (temServiceWorker) {
            try {
                const registration = await navigator.serviceWorker.getRegistration();
                const subscription = await registration?.pushManager.getSubscription();
                if (subscription) {
                    temInscricao = true;
                    endpoint = subscription.endpoint;
                }
            } catch {
                // não conseguiu checar - fica false mesmo, não trava a tela
            }
        }

        setAmbiente({
            standalone,
            temServiceWorker,
            temPushManager,
            temNotification,
            permissao,
            temInscricao,
            endpoint,
            userAgent: navigator.userAgent,
        });
    }

    async function testar() {
        setTestando(true);
        setResultado(null);
        const r = await registrarPush(user.codfun, user.tipfun, api);
        setResultado(r);
        setTestando(false);
        coletarAmbiente();
    }

    function copiarDiagnostico() {
        const linhas = [
            `Colaborador: ${user?.nomfun} (codfun ${user?.codfun})`,
            `Instalado como app (standalone): ${ambiente?.standalone ? "Sim" : "Não"}`,
            `Suporte a Service Worker: ${ambiente?.temServiceWorker ? "Sim" : "Não"}`,
            `Suporte a PushManager: ${ambiente?.temPushManager ? "Sim" : "Não"}`,
            `Permissão de notificação: ${ambiente?.permissao}`,
            `Já tem inscrição de push: ${ambiente?.temInscricao ? "Sim" : "Não"}`,
            `Endpoint: ${ambiente?.endpoint || "-"}`,
            `Navegador: ${ambiente?.userAgent}`,
            resultado ? `Último teste: ${resultado.ok ? "OK" : `FALHOU - ${resultado.motivo} - ${MOTIVOS_FALHA_PUSH[resultado.motivo] || ""}`}` : "",
            resultado?.err?.message ? `Detalhe técnico: ${resultado.err.message}` : "",
            resultado?.msg ? `Mensagem do servidor: ${resultado.msg}` : "",
        ].filter(Boolean).join("\n");

        navigator.clipboard?.writeText(linhas)
            .then(() => alert("Diagnóstico copiado - pode colar numa mensagem."))
            .catch(() => alert("Não foi possível copiar automaticamente."));
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button className={styles.btnVoltar} onClick={() => navigate(-1)}><BackIcon /></button>
                <div className={styles.headerTexto}>
                    <h2>Diagnóstico de notificações</h2>
                    <p>Veja por que este aparelho não está recebendo push</p>
                </div>
            </div>

            <div className={styles.card}>
                <span className={styles.cardTitulo}>📱 Ambiente deste aparelho</span>

                {!ambiente ? (
                    <span className={styles.dica}>Verificando...</span>
                ) : (
                    <>
                        <div className={styles.linha}>
                            <span className={styles.linhaLabel}>App instalado (tela de início)</span>
                            <span className={`${styles.linhaValor} ${ambiente.standalone ? styles.ok : styles.falha}`}>
                                {ambiente.standalone ? "Sim" : "Não"}
                            </span>
                        </div>
                        <div className={styles.linha}>
                            <span className={styles.linhaLabel}>Suporte a notificação push</span>
                            <span className={`${styles.linhaValor} ${ambiente.temServiceWorker && ambiente.temPushManager ? styles.ok : styles.falha}`}>
                                {ambiente.temServiceWorker && ambiente.temPushManager ? "Sim" : "Não"}
                            </span>
                        </div>
                        <div className={styles.linha}>
                            <span className={styles.linhaLabel}>Permissão de notificação</span>
                            <span className={`${styles.linhaValor} ${ambiente.permissao === "granted" ? styles.ok : styles.falha}`}>
                                {ambiente.permissao === "granted" ? "Concedida" : ambiente.permissao === "denied" ? "Negada" : "Não decidida"}
                            </span>
                        </div>
                        <div className={styles.linha}>
                            <span className={styles.linhaLabel}>Inscrição de push ativa</span>
                            <span className={`${styles.linhaValor} ${ambiente.temInscricao ? styles.ok : styles.falha}`}>
                                {ambiente.temInscricao ? "Sim" : "Não"}
                            </span>
                        </div>
                    </>
                )}
            </div>

            <div className={styles.card}>
                <span className={styles.cardTitulo}>🔧 Testar registro</span>
                <p className={styles.dica}>
                    Roda o mesmo processo que acontece no login, aqui na tela, e mostra o resultado exato.
                </p>

                <ApiButton className={styles.btnTestar} onClick={testar} disabled={testando}>
                    {testando ? "Testando..." : "Testar agora"}
                </ApiButton>

                {resultado && (
                    <div className={`${styles.resultadoBox} ${resultado.ok ? styles.resultadoOk : styles.resultadoErro}`}>
                        {resultado.ok ? (
                            "✅ Registrado com sucesso."
                        ) : (
                            <>
                                ❌ {MOTIVOS_FALHA_PUSH[resultado.motivo] || resultado.motivo}
                                {resultado.msg && <span className={styles.resultadoDetalhe}>Servidor: {resultado.msg}</span>}
                                {resultado.err?.message && <span className={styles.resultadoDetalhe}>Detalhe: {resultado.err.message}</span>}
                            </>
                        )}
                    </div>
                )}

                <button onClick={copiarDiagnostico} className={styles.btnCopiar}>
                    📋 Copiar diagnóstico completo
                </button>
            </div>

            <BottomNav />
        </div>
    );
}
